import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ScienceSummaries.page.css';
import MainTopBar from '../../ui/panels/TopBars/MainTopBar/MainTopBar';
import LoadingSpinner from '../../ui/components/LoadingSpinner';
import DifficultyBadge from './components/DifficultyBadge';
import BugButton from '../../ui/components/BugButton/BugButton';
import FeedBackButton from '../../ui/components/FeedBackButton/FeedBackButton';
import { LoginState } from '../../types/LoginState';
import { UserUtils } from '../../utils/UserUtils';

interface ScienceSummary {
  difficulty: number;
  conclusion: string;
  keyConcepts: string;
  sixthGradeExplanation: string;
  highSchoolExplanation: string;
  collegeFreshmanExplanation: string;
}

const ScienceSummariesPage: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [summaries, setSummaries] = useState<ScienceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasResults, setHasResults] = useState(false);
  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });
  const [selectablePackages, setSelectablePackages] = useState<string[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [threshold, setThreshold] = useState(50);

  const token = localStorage.getItem('token');
  const currentUserName = UserUtils.getCurrentUserName();

  useEffect(() => {
    validateUserLogin();
    getValidPackages();
  }, []);

  const validateUserLogin = async () => {
    if (token) {
      const url = '/validate';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        setLoginState({ isLoggedIn: false, isAdmin: false });
        return;
      }
      const json = await response.json();

      if (json.valid && json.valid === true) {
        setLoginState({ isLoggedIn: true, isAdmin: json.isAdmin || false });
      } else {
        setLoginState({ isLoggedIn: false, isAdmin: false });
      }
    }
  };

  const getValidPackages = async () => {
    const url = '/validpackages';
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/signin');
        }
        throw new Error(`Network response was not ok`);
      }
      const json = await response.json();
      const packages: string[] = json;
      setSelectablePackages(packages);
      setSelectedPackages([packages[0]]); // Default to first package
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      setError('Please enter a topic or question to analyze');
      return;
    }

    if (selectedPackages.length === 0) {
      setError('Please select at least one package');
      return;
    }

    setIsLoading(true);
    setError('');
    setHasResults(false);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch('/science-summary/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query,
          selectedPackageIds: selectedPackages,
          threshold,
        }),
      });

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate summaries');
      }

      setSummaries(data.summaries);
      setHasResults(true);
      setError('');
    } catch (err: any) {
      setError(err.message || 'An error occurred while generating summaries');
      setSummaries([]);
      setHasResults(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setSummaries([]);
    setError('');
    setHasResults(false);
  };

  return (
    <div className="science-summaries-page">
      <MainTopBar
        isLoggedIn={loginState.isLoggedIn}
        isSticky={true}
        showAdminButton={loginState.isAdmin}
        userName={currentUserName}
      />

      <div className="science-summaries-container">
        <div className="science-summaries-header">
          <h1>Science Summaries</h1>
          <p className="science-summaries-description">
            Analyze scientific text and get explanations at different
            educational levels
          </p>
        </div>

        <form onSubmit={handleSubmit} className="science-summaries-form">
          <div className="form-group">
            <label htmlFor="query">Topic or Question</label>
            <input
              id="query"
              type="text"
              className="science-query-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter a scientific topic or question (e.g., 'photosynthesis', 'CRISPR gene editing')..."
              disabled={isLoading}
            />
            <p className="input-hint">
              Enter a topic to analyze scientific content from your selected
              packages
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="packages">Select Packages</label>
            <select
              id="packages"
              className="package-select"
              value={selectedPackages[0] || ''}
              onChange={(e) => setSelectedPackages([e.target.value])}
              disabled={isLoading || selectablePackages.length === 0}
            >
              {selectablePackages.map((pkg) => (
                <option key={pkg} value={pkg}>
                  {pkg}
                </option>
              ))}
            </select>
            <p className="input-hint">
              {selectablePackages.length === 0
                ? 'Loading packages...'
                : `Analyzing content from package: ${selectedPackages[0] || 'None'}`}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="threshold">Relevancy Threshold: {threshold}%</label>
            <input
              id="threshold"
              type="range"
              className="threshold-slider"
              min="0"
              max="100"
              step="5"
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              disabled={isLoading}
            />
            <p className="input-hint">
              Higher threshold = more relevant results (recommended: 40-60%)
            </p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={
                isLoading || !query.trim() || selectedPackages.length === 0
              }
            >
              {isLoading ? 'Analyzing...' : 'Generate Summaries'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClear}
              disabled={isLoading}
            >
              Clear
            </button>
          </div>
        </form>

        {isLoading && (
          <div className="loading-container">
            <LoadingSpinner />
            <p>Analyzing scientific text...</p>
          </div>
        )}

        {hasResults && summaries.length > 0 && (
          <div className="results-container">
            <h2>Analysis Results</h2>
            <p className="results-info">
              Found {summaries.length} key conclusion
              {summaries.length !== 1 ? 's' : ''}
            </p>

            <div className="table-container">
              <table className="summaries-table">
                <thead>
                  <tr>
                    <th>Difficulty</th>
                    <th>Conclusion</th>
                    <th>Key Concepts</th>
                    <th>6th Grade Explanation</th>
                    <th>High School Explanation</th>
                    <th>College Freshman Explanation</th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.map((summary, index) => (
                    <tr key={index}>
                      <td className="difficulty-cell">
                        <DifficultyBadge level={summary.difficulty} />
                      </td>
                      <td className="conclusion-cell">{summary.conclusion}</td>
                      <td className="concepts-cell">{summary.keyConcepts}</td>
                      <td className="explanation-cell">
                        {summary.sixthGradeExplanation}
                      </td>
                      <td className="explanation-cell">
                        {summary.highSchoolExplanation}
                      </td>
                      <td className="explanation-cell">
                        {summary.collegeFreshmanExplanation}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <BugButton
        parentPageUrl="/science-summaries"
        token={token}
        nav={navigate}
      />
      <FeedBackButton
        parentPageUrl="/science-summaries"
        token={token}
        nav={navigate}
      />
    </div>
  );
};

export default ScienceSummariesPage;
